import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContactDialog from '../components/ContactDialog';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BRAND_COLOR } from '../theme';
import { analytics, AnalyticsEvent } from '../services/analytics';

const PageContainer = styled(Box)(() => ({
    minHeight: '100vh',
    backgroundColor: '#ffffff',
}));

const HeroSection = styled(Box)(({ theme }) => ({
    backgroundColor: BRAND_COLOR,
    color: 'white',
    padding: '120px 0 80px',
    [theme.breakpoints.down('md')]: {
        padding: '100px 0 60px',
    },
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url("/images/vertical-background.svg")',
        opacity: 0.15,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    },
}));

const DemoCard = styled(Card)(() => ({
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'visible',
    border: '1px solid rgba(0, 0, 0, 0.05)',
}));

const PodcastRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px',
    borderRadius: '12px',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: 'rgba(26, 26, 46, 0.02)',
        borderColor: BRAND_COLOR,
    },
}));

const RankBadge = styled(Box)(() => ({
    width: 32,
    height: 32,
    borderRadius: '8px',
    backgroundColor: BRAND_COLOR,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.9rem',
}));

const PodcastAvatar = styled(Box)(() => ({
    width: 56,
    height: 56,
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
}));

const FeatureCard = styled(Box)(() => ({
    padding: '32px',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    },
}));

const FeatureIconWrapper = styled(Box)(() => ({
    width: 56,
    height: 56,
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    background: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
}));

// Fake data for the mockup
const fakePodcasts = [
    {
        id: 1,
        rank: 1,
        name: 'El Podcast de Chile',
        emoji: '🎙️',
        category: 'Actualidad',
        avgViews: 245000,
        subscribers: '1.2M',
        brands: ['Banco', 'Claro', 'Falabella'],
        trend: '+12%',
    },
    {
        id: 2,
        rank: 2,
        name: 'Conversaciones Reales',
        emoji: '💬',
        category: 'Entrevistas',
        avgViews: 198000,
        subscribers: '890K',
        brands: ['Entel', 'Paris'],
        trend: '+8%',
    },
    {
        id: 3,
        rank: 3,
        name: 'Tech & Negocios',
        emoji: '💼',
        category: 'Negocios',
        avgViews: 156000,
        subscribers: '650K',
        brands: ['AWS', 'Mercado Libre', 'Bci'],
        trend: '+15%',
    },
    {
        id: 4,
        rank: 4,
        name: 'Cultura Pop Chile',
        emoji: '🎬',
        category: 'Entretenimiento',
        avgViews: 134000,
        subscribers: '520K',
        brands: ['Netflix', 'Spotify'],
        trend: '+5%',
    },
    {
        id: 5,
        rank: 5,
        name: 'Historias del Sur',
        emoji: '📚',
        category: 'Historia',
        avgViews: 98000,
        subscribers: '380K',
        brands: ['Banco', 'Copec'],
        trend: '+22%',
    },
];

const fakeLivestreams = [
    {
        id: 1,
        rank: 1,
        name: 'Hablemos Sin Saber',
        emoji: '🎤',
        category: 'Entretenimiento',
        avgViewers: 45000,
        peakViewers: '89K',
        duration: '2h 15m',
        trend: '+25%',
    },
    {
        id: 2,
        rank: 2,
        name: 'Charlando con Amigos',
        emoji: '💬',
        category: 'Conversación',
        avgViewers: 32000,
        peakViewers: '58K',
        duration: '1h 45m',
        trend: '+12%',
    },
    {
        id: 3,
        rank: 3,
        name: 'Gaming Chile Live',
        emoji: '🎮',
        category: 'Gaming',
        avgViewers: 28000,
        peakViewers: '52K',
        duration: '3h 30m',
        trend: '+8%',
    },
    {
        id: 4,
        rank: 4,
        name: 'Noticias en Vivo',
        emoji: '📰',
        category: 'Noticias',
        avgViewers: 21000,
        peakViewers: '35K',
        duration: '1h 00m',
        trend: '+5%',
    },
    {
        id: 5,
        rank: 5,
        name: 'Deportes Chile',
        emoji: '⚽',
        category: 'Deportes',
        avgViewers: 18000,
        peakViewers: '42K',
        duration: '2h 00m',
        trend: '+10%',
    },
];

const fakeBrands = [
    {
        id: 1,
        rank: 1,
        name: 'Banco',
        emoji: '🏦',
        programs: 12,
        mentions: 45,
        totalViews: 2450000,
        trend: '+18%',
    },
    {
        id: 2,
        rank: 2,
        name: 'Claro',
        emoji: '📱',
        programs: 8,
        mentions: 32,
        totalViews: 1870000,
        trend: '+12%',
    },
    {
        id: 3,
        rank: 3,
        name: 'Falabella',
        emoji: '🛍️',
        programs: 6,
        mentions: 28,
        totalViews: 1560000,
        trend: '+8%',
    },
    {
        id: 4,
        rank: 4,
        name: 'Netflix',
        emoji: '🎬',
        programs: 5,
        mentions: 22,
        totalViews: 1320000,
        trend: '+15%',
    },
    {
        id: 5,
        rank: 5,
        name: 'Entel',
        emoji: '📡',
        programs: 4,
        mentions: 18,
        totalViews: 980000,
        trend: '+5%',
    },
];

const ExplorarMockup: React.FC = () => {
    const [contactOpen, setContactOpen] = useState(false);

    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_EXPLORAR_PAGE);
    }, []);

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
        return num.toString();
    };

    return (
        <PageContainer>
            {/* Hero Section */}
            <HeroSection>
                <Container
                    maxWidth="lg"
                    sx={{ position: 'relative', zIndex: 1, px: { xs: 3, md: 4 } }}
                >
                    <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2.2rem', md: '3.2rem' },
                                fontWeight: 800,
                                fontFamily: '"DM Serif Display", serif',
                                mb: 2,
                                color: 'white',
                            }}
                        >
                            Explorador de Programas
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                color: 'rgba(255, 255, 255, 0.85)',
                                mb: 4,
                                lineHeight: 1.6,
                            }}
                        >
                            Descubre, compara y analiza programas de conversación en YouTube.
                            Encuentra oportunidades de inversión y posicionamiento de marca.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Chip
                                icon={<YouTubeIcon />}
                                label="+100 Programas"
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' },
                                }}
                            />
                            <Chip
                                icon={<BusinessIcon />}
                                label="+300 Marcas"
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' },
                                }}
                            />

                            <Chip
                                icon={<TrendingUpIcon />}
                                label="Actualizado Semanalmente"
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' },
                                }}
                            />
                        </Box>
                    </Box>
                </Container>
            </HeroSection>

            {/* Demo Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, px: { xs: 3, md: 4 } }}>
                <Grid container spacing={3}>
                    {/* Podcasts Column */}
                    <Grid item xs={12} md={6}>
                        <DemoCard sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 2,
                                        color: BRAND_COLOR,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TrendingUpIcon /> Top Podcasts
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {fakePodcasts.map(podcast => (
                                        <PodcastRow key={podcast.id} sx={{ p: 1.5 }}>
                                            <RankBadge>{podcast.rank}</RankBadge>
                                            <PodcastAvatar sx={{ width: 44, height: 44 }}>
                                                {podcast.emoji}
                                            </PodcastAvatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        color: BRAND_COLOR,
                                                        mb: 0.3,
                                                    }}
                                                >
                                                    {podcast.name}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: 'rgba(0, 0, 0, 0.5)',
                                                    }}
                                                >
                                                    {podcast.subscribers} subs
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.95rem',
                                                        color: BRAND_COLOR,
                                                    }}
                                                >
                                                    {formatNumber(podcast.avgViews)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        color: '#22c55e',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {podcast.trend}
                                                </Typography>
                                            </Box>
                                        </PodcastRow>
                                    ))}
                                </Box>
                            </CardContent>
                        </DemoCard>
                    </Grid>

                    {/* Livestreams Column */}
                    <Grid item xs={12} md={6}>
                        <DemoCard sx={{ height: '100%' }}>
                            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 2,
                                        color: BRAND_COLOR,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <TrendingUpIcon /> Top Livestreams
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {fakeLivestreams.map(stream => (
                                        <PodcastRow key={stream.id} sx={{ p: 1.5 }}>
                                            <RankBadge>{stream.rank}</RankBadge>
                                            <PodcastAvatar sx={{ width: 44, height: 44 }}>
                                                {stream.emoji}
                                            </PodcastAvatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.9rem',
                                                        color: BRAND_COLOR,
                                                        mb: 0.3,
                                                    }}
                                                >
                                                    {stream.name}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: 'rgba(0, 0, 0, 0.5)',
                                                    }}
                                                >
                                                    {stream.duration}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right' }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '0.95rem',
                                                        color: BRAND_COLOR,
                                                    }}
                                                >
                                                    {formatNumber(stream.avgViewers)}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        color: '#22c55e',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {stream.trend}
                                                </Typography>
                                            </Box>
                                        </PodcastRow>
                                    ))}
                                </Box>
                            </CardContent>
                        </DemoCard>
                    </Grid>
                </Grid>

                {/* Brands Section */}
                <Box sx={{ mt: 4 }}>
                    <DemoCard>
                        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                            <Typography
                                sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    fontFamily: '"DM Serif Display", serif',
                                    mb: 2,
                                    color: BRAND_COLOR,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <BusinessIcon /> Presencia de Marcas
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {fakeBrands.map(brand => (
                                    <PodcastRow key={brand.id} sx={{ p: 1.5 }}>
                                        <RankBadge>{brand.rank}</RankBadge>
                                        <PodcastAvatar sx={{ width: 44, height: 44 }}>
                                            {brand.emoji}
                                        </PodcastAvatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.9rem',
                                                    color: BRAND_COLOR,
                                                }}
                                            >
                                                {brand.name}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                px: 1,
                                                display: { xs: 'none', sm: 'block' },
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.95rem',
                                                    color: BRAND_COLOR,
                                                }}
                                            >
                                                {brand.programs}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'rgba(0, 0, 0, 0.5)',
                                                }}
                                            >
                                                programas
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', px: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.95rem',
                                                    color: BRAND_COLOR,
                                                }}
                                            >
                                                {brand.mentions}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'rgba(0, 0, 0, 0.5)',
                                                }}
                                            >
                                                menciones
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.95rem',
                                                    color: BRAND_COLOR,
                                                }}
                                            >
                                                {formatNumber(brand.totalViews)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    color: '#22c55e',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {brand.trend}
                                            </Typography>
                                        </Box>
                                    </PodcastRow>
                                ))}
                            </Box>
                        </CardContent>
                    </DemoCard>
                </Box>
            </Container>

            {/* Features Section */}
            <Box sx={{ backgroundColor: BRAND_COLOR, py: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '1.8rem', md: '2.2rem' },
                            fontWeight: 800,
                            fontFamily: '"DM Serif Display", serif',
                            textAlign: 'center',
                            mb: 2,
                            color: 'white',
                        }}
                    >
                        ¿Qué puedes hacer con el Explorador?
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(255, 255, 255, 0.7)',
                            mb: 6,
                            maxWidth: '600px',
                            mx: 'auto',
                        }}
                    >
                        Herramientas para entender el ecosistema de programas en YouTube y tomar
                        mejores decisiones.
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <CompareArrowsIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Comparar Programas
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Compara audiencia, engagement y presencia de marcas entre
                                    diferentes programas lado a lado.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <BusinessIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Rastrear Marcas
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Descubre qué marcas están activas en programas de YouTube, en
                                    qué canales aparecen y con qué frecuencia.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FeatureCard>
                                <FeatureIconWrapper>
                                    <VisibilityIcon sx={{ fontSize: 28 }} />
                                </FeatureIconWrapper>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        fontWeight: 700,
                                        fontFamily: '"DM Serif Display", serif',
                                        mb: 1.5,
                                        color: 'white',
                                    }}
                                >
                                    Encontrar Oportunidades
                                </Typography>
                                <Typography
                                    sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.7 }}
                                >
                                    Identifica programas en crecimiento y categorías con poca
                                    presencia de marcas para invertir.
                                </Typography>
                            </FeatureCard>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA Section */}
            <Box sx={{ backgroundColor: '#ffffff', py: { xs: 8, md: 10 } }}>
                <Container maxWidth="md" sx={{ textAlign: 'center', px: { xs: 3, md: 4 } }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '1.8rem', md: '2.2rem' },
                            fontWeight: 800,
                            fontFamily: '"DM Serif Display", serif',
                            color: BRAND_COLOR,
                            mb: 2,
                        }}
                    >
                        ¿Listo para explorar el mercado?
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(26, 26, 46, 0.6)',
                            mb: 4,
                            fontSize: '1.1rem',
                        }}
                    >
                        Accede a la base de datos más completa de programas y marcas en Chile.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => {
                            analytics.track(AnalyticsEvent.CLICK_REQUEST_ACCESS, {
                                location: 'explorar_page',
                            });
                            setContactOpen(true);
                        }}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            backgroundColor: BRAND_COLOR,
                            color: 'white',
                            fontWeight: 700,
                            textTransform: 'none',
                            px: 5,
                            py: 1.8,
                            borderRadius: '14px',
                            fontSize: '1.05rem',
                            '&:hover': {
                                backgroundColor: BRAND_COLOR,
                            },
                        }}
                    >
                        Solicitar acceso al explorador
                    </Button>
                </Container>
            </Box>

            <ContactDialog
                open={contactOpen}
                onClose={() => setContactOpen(false)}
                title="Solicitar acceso al explorador"
                subtitle="Completa el formulario y te contactaremos para entender tu necesidad
y evaluar el acceso al explorador."
            />
        </PageContainer>
    );
};

export default ExplorarMockup;
